import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Heart } from 'lucide-react';
import { favoritesService } from '../../services/favorites';
import { GoodThingCard } from '../../components/good-things/GoodThingCard';
import { GoodThing } from '../../types/good-things';
import { useAuth } from '../../context/AuthContext';
import { FavoriteButton } from '../../components/good-things/FavoriteButton';
import { Skeleton } from '../../components/ui/skeleton';
import { GoodThingDialog } from '../../components/good-things/GoodThingDialog';
import { GoodThingsGrid } from '../../components/good-things/GoodThingsGrid';
import { GoodThingsLine } from '../../components/good-things/GoodThingsLine';
import { LayoutToggle, LayoutType } from '../../components/good-things/LayoutToggle';
import { ToggleGroup, ToggleGroupItem } from '../../components/ui/toggle-group';
import { employeeNamesService } from '../../services/employeeNames';

type FilterType = 'received' | 'entered' | 'other' | 'all';

const Favorites = () => {
  usePageTitle('Good Thing Tracker');
  const [favorites, setFavorites] = useState<GoodThing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [layoutType, setLayoutType] = useState<LayoutType>('grid');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [userName, setUserName] = useState<string | null>(null);
  const { userEmail } = useAuth();

  // Fetch user's name from email
  useEffect(() => {
    const fetchUserName = async () => {
      if (!userEmail) return;
      try {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        setUserName(name);
      } catch (error) {
        
      }
    };
    
    fetchUserName();
  }, [userEmail]);

  // Function to fetch favorites
  const fetchFavorites = async () => {
    if (!userEmail) return;
    try {
      setIsLoading(true);
      const data = await favoritesService.getFavorites(userEmail);
      // Sort by favoritedAt date
      const sortedData = data.sort((a, b) => 
        new Date(b.favoritedAt).getTime() - new Date(a.favoritedAt).getTime()
      );
      setFavorites(sortedData);
    } catch (error) {
      
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch favorites on mount
  useEffect(() => {
    fetchFavorites();
  }, [userEmail]);

  const handleUnfavorite = (goodThingId: string) => {
    setFavorites(prevFavorites => prevFavorites.filter(favorite => favorite.id !== goodThingId));
  };

  const handleDialogSuccess = () => {
    setDialogOpen(false);
    window.location.reload(); // Refresh to show new entry
  };

  // Filter favorites based on the active filter
  const filteredFavorites = useMemo(() => {
    if (!userName || activeFilter === 'all') {
      return favorites;
    }

    // Create name variations to handle different formats
    const nameVariations = [userName];
    if (userName.endsWith('.')) {
      nameVariations.push(userName.slice(0, -1));
    } else {
      nameVariations.push(`${userName}.`);
    }

    switch (activeFilter) {
      case 'received':
        return favorites.filter(favorite => 
          favorite.recipients && favorite.recipients.some(recipient => 
            nameVariations.some(name => recipient === name)
          )
        );
      case 'entered':
        return favorites.filter(favorite => 
          nameVariations.some(name => favorite.submitter === name)
        );
      case 'other':
        return favorites.filter(favorite => {
          const isRecipient = favorite.recipients && favorite.recipients.some(recipient => 
            nameVariations.some(name => recipient === name)
          );
          const isSubmitter = nameVariations.some(name => favorite.submitter === name);
          return !isRecipient && !isSubmitter;
        });
      default:
        return favorites;
    }
  }, [favorites, userName, activeFilter]);

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6 md:mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Favorites</h1>
          <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">Your collection of favorite Good Things</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Only show layout toggle on desktop */}
          <div className="hidden md:block">
            <LayoutToggle 
              onLayoutChange={setLayoutType} 
              className="mr-2"
            />
          </div>
          <GoodThingDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            onSuccess={handleDialogSuccess}
          />
        </div>
      </div>

      {/* Filter Toggle - Desktop */}
      <div className="hidden md:flex justify-center mb-6">
        <ToggleGroup 
          type="single" 
          value={activeFilter}
          onValueChange={(value) => {
            if (value) setActiveFilter(value as FilterType);
          }}
          className="mb-4"
        >
          <ToggleGroupItem value="all">All Favorites</ToggleGroupItem>
          <ToggleGroupItem value="received">Good Things You've Received</ToggleGroupItem>
          <ToggleGroupItem value="entered">Good Things You've Entered</ToggleGroupItem>
          <ToggleGroupItem value="other">Other Good Things</ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Filter Toggle - Mobile */}
      <div className="md:hidden mb-6">
        <select 
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value as FilterType)}
          className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="all">All Favorites</option>
          <option value="received">Good Things You've Received</option>
          <option value="entered">Good Things You've Entered</option>
          <option value="other">Other Good Things</option>
        </select>
      </div>

      {/* Favorites List */}
      {layoutType === 'grid' ? (
        <GoodThingsGrid>
        {isLoading ? (
          // Loading skeletons
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-4">
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="h-6 w-6 rounded-full" />
                </div>
                <Skeleton className="h-16 w-full mb-4" />
                <div className="flex gap-2">
                  <Skeleton className="h-8 w-32 rounded-full" />
                  <Skeleton className="h-8 w-48 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ))
        ) : filteredFavorites.length > 0 ? (
          filteredFavorites.map((favorite) => (
            <GoodThingCard 
              key={favorite.id} 
              goodThing={favorite} 
              onUnfavorite={() => handleUnfavorite(favorite.id)}
              onEdit={fetchFavorites}
              onDelete={fetchFavorites}
            />
          ))
        ) : (
          <div className="text-center py-12 col-span-2">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900 mb-4">
              <Heart className="h-8 w-8 text-pink-500 dark:text-pink-300" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
              {activeFilter === 'all' ? 'No favorites yet' : 
               activeFilter === 'received' ? 'No received Good Things in favorites' :
               activeFilter === 'entered' ? 'No entered Good Things in favorites' :
               'No other Good Things in favorites'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {activeFilter === 'all' ? 'Click the heart icon on any Good Thing to add it to your favorites' :
               'Try selecting a different filter or add more Good Things to your favorites'}
            </p>
          </div>
        )}
        </GoodThingsGrid>
      ) : (
        <GoodThingsLine>
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-4">
                    <div className="space-y-3">
                      <Skeleton className="h-6 w-24" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                    <Skeleton className="h-6 w-6 rounded-full" />
                  </div>
                  <Skeleton className="h-16 w-full mb-4" />
                  <div className="flex gap-2">
                    <Skeleton className="h-8 w-32 rounded-full" />
                    <Skeleton className="h-8 w-48 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : filteredFavorites.length > 0 ? (
            filteredFavorites.map((favorite) => (
              <GoodThingCard 
                key={favorite.id} 
                goodThing={favorite} 
                onUnfavorite={() => handleUnfavorite(favorite.id)}
                onEdit={fetchFavorites}
                onDelete={fetchFavorites}
              />
            ))
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900 mb-4">
                <Heart className="h-8 w-8 text-pink-500 dark:text-pink-300" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                {activeFilter === 'all' ? 'No favorites yet' : 
                 activeFilter === 'received' ? 'No received Good Things in favorites' :
                 activeFilter === 'entered' ? 'No entered Good Things in favorites' :
                 'No other Good Things in favorites'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {activeFilter === 'all' ? 'Click the heart icon on any Good Thing to add it to your favorites' :
                 'Try selecting a different filter or add more Good Things to your favorites'}
              </p>
            </div>
          )}
        </GoodThingsLine>
      )}
    </div>
  );
};

export default Favorites;
